import React, { useEffect } from "react";

import {
	faCheck,
	faCopy,
	faEdit,
	faExternalLink,
	faSpinner,
	faTrashCan,
	faUnlink,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faLinkEmbed, faLinkPill, faLinkText } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import cx from "classnames";
import copy from "copy-to-clipboard";
import { noop } from "lodash";
import { fetchQuery, graphql } from "relay-runtime";

import useOnChange from "~/lib/hooks/useOnChange";
import { addHttpsToNonUrls } from "~/lib/utils";
import { environment } from "~/models";
import { AnchorButton, toast, Tooltip } from "~/ui";
import { LabelIconForButton } from "~/ui/button";
import IconButton from "~/ui/button/IconButton";
import { Input } from "~/ui/legacyFellowIndex";
import { useSingleton } from "~/ui/Tooltip";

import { detectService } from "../plugins/integrations/helpers";

import type { linkToolsGetURLIsOkQuery } from "~/__generated__/linkToolsGetURLIsOkQuery.graphql";

import styles from "./linkTools.module.css";

type DisplayMode = "text" | "linkPill" | "embedded";

type DumbLinkToolbarProps = {
	url: string;
	text: string;
	displayMode: DisplayMode;
	editShowText: boolean;
	onEditClick?: () => void;
	onEditSubmit?: (url: string, text: string) => void;
	onUnlink?: () => void;
	onRemove?: () => void;
	onConvertToPill?: () => void;
	onConvertToText?: () => void;
	onConvertToEmbed?: () => void;
	canCopy: boolean;
	captureMouseDown?: boolean;
	insertingNewLink?: boolean;
};

function LinkToolBarEdit({
	onEditSubmit,
	showText,
	editingText,
	editingUrl,
	onUnlink,
	onURLChange,
	onTextChange,
	onExit,
}: Pick<DumbLinkToolbarProps, "onEditSubmit"> & {
	showText: boolean;
	editingText: string;
	editingUrl: string;
	onUnlink: () => void;
	onExit: () => void;
	onURLChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
	const onSubmit = React.useCallback(() => {
		onEditSubmit?.(addHttpsToNonUrls(editingUrl), editingText);
		onExit();
	}, [onEditSubmit, onExit, editingText, editingUrl]);
	const onKeyPress = React.useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				onExit();
			} else if (e.key === "Enter") {
				onSubmit();
			}
		},
		[onSubmit, onExit],
	);

	const titleInputRef = React.useRef<HTMLInputElement>(null);
	const urlInputRef = React.useRef<HTMLInputElement>(null);

	// Autofocus wasn't working with the CMD+K shortcut for the toolbar, so we have to revert to focusing manually
	// when the component first renders
	React.useEffect(() => {
		if (!editingText || (!!editingText && !!editingUrl)) {
			titleInputRef.current?.focus();
		} else {
			urlInputRef.current?.focus();
		}
		// We intentionally don't want this to run on re-renders
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={styles.linkEdit}>
			<div className={styles.inputs} onKeyUp={onKeyPress}>
				{showText && (
					<Input
						name="link_text"
						className={cx(styles.linkToolbarTextInput, styles.linkToolbarTextInput)}
						value={editingText}
						onChange={onTextChange}
						autoComplete="off"
						autoCorrect="off"
						autoCapitalize="off"
						spellCheck={false}
						placeholder="Type your text here"
						data-editor-input
						innerRef={titleInputRef}
					/>
				)}
				<Input
					name="link_url"
					type="url"
					value={editingUrl}
					onChange={onURLChange}
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="off"
					spellCheck={false}
					placeholder="Type or paste your link here"
					data-editor-input
					innerRef={urlInputRef}
				/>
			</div>
			<div className={styles.linkEditButtons}>
				<IconButton type="button" onClick={onSubmit} icon={faCheck} size="large" />
				{showText && <IconButton type="button" icon={faUnlink} size="large" onClick={onUnlink} />}
			</div>
		</div>
	);
}

interface EmbedButtonProps {
	url: string;
	cb?: (e: React.MouseEvent) => void;
	focused?: boolean;
	tooltipSingleton?: ReturnType<typeof useSingleton>[1];
}

const getURLIsOkQuery = graphql`
	query linkToolsGetURLIsOkQuery($url: String!) {
		getUrlIsOk(url: $url) {
			isOk
		}
	}
`;

export const useGetURLIsValid = (url: string) => {
	const [isValid, setIsValid] = React.useState<boolean | null>(null);

	useEffect(() => {
		const onURLChange = async () => {
			const service = detectService(url);

			fetchQuery<linkToolsGetURLIsOkQuery>(environment, getURLIsOkQuery, { url })
				.toPromise()
				.then(result => {
					const isOK = !!result && !!result.getUrlIsOk?.isOk;
					setIsValid(!!service && isOK);
				});
		};

		onURLChange();
	}, [url]);

	return isValid;
};

export function EmbedButton({ url, cb, focused, tooltipSingleton }: EmbedButtonProps) {
	const isValid = useGetURLIsValid(url);

	let tooltip = "Embed";
	if (isValid === false) {
		tooltip = "Embed not supported for this URL";
	} else if (isValid === null) {
		tooltip = "Checking...";
	}

	return (
		<Tooltip content={tooltip} placement={isValid === false ? "bottom-start" : "bottom"} singleton={tooltipSingleton}>
			<div>
				<IconButton
					disabled={!isValid}
					className={cx({
						[styles.displayModeCurrent]: !!focused,
					})}
					onClick={cb}
					icon={isValid === null ? faSpinner : faLinkEmbed}
					size="large"
					variant="tertiary"
					iconProps={
						isValid === null
							? {
									spinPulse: true,
								}
							: undefined
					}
				/>
			</div>
		</Tooltip>
	);
}

export function DumbLinkToolBar({
	url,
	text,
	displayMode,
	editShowText,
	onEditClick,
	onEditSubmit,
	onUnlink,
	onRemove,
	onConvertToPill,
	onConvertToText,
	onConvertToEmbed,
	captureMouseDown,
	insertingNewLink,
}: DumbLinkToolbarProps) {
	const [editing, _setEditing] = React.useState((text && !url) || insertingNewLink);
	const [editingText, setEditingText] = React.useState(url === text ? "" : text);
	const [editingUrl, setEditingUrl] = React.useState(url);
	const onURLChange = useOnChange<HTMLInputElement>(setEditingUrl);
	const onTextChange = useOnChange<HTMLInputElement>(setEditingText);
	const setEditingTrue = React.useCallback(() => {
		_setEditing(true);
	}, [_setEditing]);
	const setEditingFalse = React.useCallback(() => {
		_setEditing(false);
	}, [_setEditing]);
	const onCopy = React.useCallback(() => {
		if (!url) return;
		copy(url);
		toast.info(`Copied to clipboard!`, { autoClose: 5000 });
	}, [url]);
	const modeButtons: [DisplayMode, ((e: React.MouseEvent) => void) | undefined, IconProp, string][] =
		React.useMemo(() => {
			function makeEventListener(cb?: () => void) {
				function listener(event: React.MouseEvent) {
					if (captureMouseDown) {
						event.stopPropagation();
					}
					cb?.();
				}
				if (cb) return listener;
				return undefined;
			}
			return [
				// eslint-disable-next-line react/jsx-key
				["text", makeEventListener(onConvertToText), faLinkText, "URL"],
				// eslint-disable-next-line react/jsx-key
				["linkPill", makeEventListener(onConvertToPill), faLinkPill, "Inline"],
				// eslint-disable-next-line react/jsx-key
				["embedded", makeEventListener(onConvertToEmbed), faLinkEmbed, "Embed"],
			];
		}, [onConvertToEmbed, onConvertToPill, onConvertToText, captureMouseDown]);
	// if the user can't convert to other types of link, no point showing the current type neither
	const convertingButtonCount = modeButtons.filter(([mode, cb]) => mode !== displayMode && cb).length;

	const [tooltipSource, tooltipTarget] = useSingleton({ overrides: ["placement"] });

	if (editing)
		return (
			<LinkToolBarEdit
				showText={editShowText}
				editingText={editingText}
				editingUrl={editingUrl}
				onTextChange={onTextChange}
				onURLChange={onURLChange}
				onEditSubmit={onEditSubmit}
				onUnlink={onUnlink || noop}
				onExit={setEditingFalse}
			/>
		);
	return (
		<div className={cx(styles.linkToolbar, { [styles.linkToolbarEditing]: editing })}>
			<Tooltip interactive singleton={tooltipSource} placement="bottom" />
			{!editing && convertingButtonCount > 0 && (
				<>
					<div className={cx(styles.actions, styles.front)}>
						{modeButtons.map(([mode, cb, icon, tooltip]) => {
							if (!cb && mode !== displayMode) return null;

							if (mode === "embedded") {
								return (
									<EmbedButton
										key={mode}
										url={url}
										cb={cb}
										focused={displayMode === mode}
										tooltipSingleton={tooltipTarget}
									/>
								);
							}

							const listener = mode === displayMode ? noop : cb;
							return (
								<Tooltip key={mode} content={tooltip} placement="bottom" singleton={tooltipTarget}>
									<IconButton
										className={cx({
											[styles.displayModeCurrent]: displayMode === mode,
										})}
										onMouseDownCapture={captureMouseDown ? listener : undefined}
										onClick={captureMouseDown ? undefined : listener}
										icon={icon}
										size="large"
										variant="tertiary"
									/>
								</Tooltip>
							);
						})}
					</div>
					<div className={cx(styles.divider)} />
				</>
			)}
			{!editing ? (
				<AnchorButton
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					title={url}
					variant="tertiary"
					className={styles.url}
				>
					Visit link
					<LabelIconForButton icon={faExternalLink} side="right" />
				</AnchorButton>
			) : null}
			<div className={styles.divider} />
			<div className={styles.actions}>
				<Tooltip content="Copy link" placement="bottom" singleton={tooltipTarget}>
					<IconButton onClick={onCopy} icon={faCopy} size="large" variant="tertiary" />
				</Tooltip>
				<Tooltip content="Edit link" placement="bottom" singleton={tooltipTarget}>
					<IconButton
						onClick={onEditClick ? onEditClick : setEditingTrue}
						disabled={!onEditSubmit && !onEditClick}
						icon={faEdit}
						size="large"
						variant="tertiary"
					/>
				</Tooltip>
				{onUnlink && (
					<Tooltip content="Unlink" placement="bottom" singleton={tooltipTarget}>
						<IconButton onClick={onUnlink} icon={faUnlink} size="large" variant="tertiary" />
					</Tooltip>
				)}
				{onRemove && (
					<Tooltip content="Delete" placement="bottom" singleton={tooltipTarget}>
						<IconButton onClick={onRemove} icon={faTrashCan} size="large" variant="tertiary" />
					</Tooltip>
				)}
			</div>
		</div>
	);
}
